import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { formatDate } from '../../../utils/formatUtils';

const COMPANY_COLORS = {
  headerBg: '5e894f',
  headerFont: 'FFFFFF',
  evenRow: 'E3E6F3',
  oddRow: 'FFFFFF',
  positiveGreen: '4caf50',
  negativeRed: 'f44336',
  paidGreen: '4caf50',
  unpaidOrange: 'ff9800',
};

export async function exportCouponsToExcel(
  coupons,
  contractInfo,
  filename = 'Cupones_Contrato.xlsx'
) {
  if (!coupons || coupons.length === 0) return;

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Cupones');

  // Configurar columnas
  const columns = [
    { header: 'Mes/Año', key: 'monthYear', width: 15 },
    { header: 'Tipo de Cuota', key: 'feeType', width: 20 },
    { header: 'Estado', key: 'status', width: 12 },
    { header: 'Fecha de Pago', key: 'paymentDate', width: 15 },
    { header: '1° Vencimiento', key: 'firstDue', width: 15 },
    { header: '1° Monto', key: 'firstAmount', width: 15 },
    { header: '2° Vencimiento', key: 'secondDue', width: 15 },
    { header: '2° Monto', key: 'secondAmount', width: 15 },
    { header: '3° Vencimiento', key: 'thirdDue', width: 15 },
    { header: '3° Monto', key: 'thirdAmount', width: 15 },
  ];

  worksheet.columns = columns;

  // Título principal
  const titleRow = worksheet.insertRow(1, [`Cupones del Contrato - ${contractInfo?.contract_number || 'N/A'}`]);
  titleRow.getCell(1).font = { bold: true, size: 14 };
  worksheet.mergeCells('A1:J1');
  titleRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };
  titleRow.height = 25;

  // Información del contrato
  if (contractInfo) {
    const infoRow = worksheet.insertRow(2, [
      `Contratista: ${contractInfo.subscriber_name || 'N/A'} | Plan: ${contractInfo.plan_name || 'N/A'}`
    ]);
    infoRow.getCell(1).font = { size: 11 };
    worksheet.mergeCells('A2:J2');
    infoRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };
    infoRow.height = 20;
  }

  // Fila vacía
  worksheet.addRow([]);

  // Encabezados
  const headerRow = worksheet.addRow(columns.map(col => col.header));
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: COMPANY_COLORS.headerBg },
    };
    cell.font = {
      color: { argb: COMPANY_COLORS.headerFont },
      bold: true,
      size: 11,
    };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  // Función auxiliar para obtener el tipo de cuota
  const getFeeDisplay = (coupon) => {
    if (coupon.fee_number1 === 0) return 'Adecuación';
    if (coupon.is_adjudication) return 'Adjudicación';
    if (coupon.is_score) return 'De Puntaje';
    if (coupon.is_advance_payment) return 'Adelanto';
    if (coupon.fee_number2 && coupon.fee_number2 !== 0) {
      return `${coupon.fee_number1} - ${coupon.fee_number2}`;
    }
    return coupon.fee_number1 || '-';
  };

  // Agregar datos de cupones
  coupons.forEach((coupon, idx) => {
    const rowData = {
      monthYear: coupon.month && coupon.year ? `${coupon.month}/${coupon.year}` : '-',
      feeType: getFeeDisplay(coupon),
      status: coupon.cancellation_date ? 'Pagado' : 'Pendiente',
      paymentDate: coupon.cancellation_date ? formatDate(coupon.cancellation_date) : '-',
      firstDue: formatDate(coupon.first_due_date),
      firstAmount: parseFloat(coupon.first_due_amount) || 0,
      secondDue: formatDate(coupon.second_due_date),
      secondAmount: parseFloat(coupon.second_due_amount) || 0,
      thirdDue: formatDate(coupon.third_due_date),
      thirdAmount: parseFloat(coupon.third_due_amount) || 0,
    };

    const row = worksheet.addRow(rowData);

    row.eachCell((cell, colNumber) => {
      const isEvenRow = idx % 2 === 0;
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: isEvenRow ? COMPANY_COLORS.evenRow : COMPANY_COLORS.oddRow },
      };

      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };

      // Formatear columna de estado con colores
      if (colNumber === 3) { // Status column
        if (coupon.cancellation_date) {
          cell.font = { color: { argb: COMPANY_COLORS.paidGreen }, bold: true };
        } else {
          cell.font = { color: { argb: COMPANY_COLORS.unpaidOrange }, bold: true };
        }
      }

      // Formatear columnas de montos
      if ([6, 8, 10].includes(colNumber)) { // Amount columns
        cell.numFmt = '[$$-es-AR] #,##0.00';
      }
    });
  });

  // Estadísticas finales
  if (coupons.length > 0) {
    const paidCoupons = coupons.filter(c => c.cancellation_date).length;
    const pendingCoupons = coupons.length - paidCoupons;
    
    // Fila vacía
    worksheet.addRow([]);
    
    // Estadísticas
    const statsRow1 = worksheet.addRow(['RESUMEN:', `Total cupones: ${coupons.length}`, '', '', '', '', '', '', '', '']);
    const statsRow2 = worksheet.addRow(['', `Cupones pagados: ${paidCoupons}`, '', '', '', '', '', '', '', '']);
    const statsRow3 = worksheet.addRow(['', `Cupones pendientes: ${pendingCoupons}`, '', '', '', '', '', '', '', '']);

    [statsRow1, statsRow2, statsRow3].forEach(row => {
      row.eachCell((cell, colNumber) => {
        if (colNumber <= 2) {
          cell.font = { bold: true };
        }
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
      });
    });
  }

  // Generar y descargar archivo
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    filename
  );
}